// frontend/src/pages/SizeCheck.jsx
import React, { useMemo, useState, useEffect, useCallback } from "react";
import AccessoryModal from "../ui/AccessoryModal";
import SizeCheckModal from "../ui/SizeCheckModal";
import { Button, TextInput, RadioGroup, ListBox, Modal } from "../ui/UIComponents";
import { render, renderFilename } from "../ui/tp_prod_ord_config";
import HDSD_DATA from "./hdsd.json";

/** Debug columns OFF (clean UI) */
const DEBUG_COLUMNS = false;

/** Robust fetch that tolerates non-JSON error bodies */
async function fetchJSON(input, init) {
  const resp = await fetch(input, init);
  try {
    const data = await resp.json();
    if (!resp.ok || data?.ok === false) {
      const msg = data?.details?.message || data?.error || `HTTP ${resp.status}`;
      const e = new Error(msg);
      e.status = resp.status;
      throw e;
    }
    return data;
  } catch {
    const txt = await resp.clone().text();
    const clean = (txt || "").slice(0, 300) || "Non-JSON error response";
    const e = new Error(clean);
    e.status = resp.status;
    throw e;
  }
}

/** Normal display label: Name — Description (restored) */
const formatPLNormal = (row) => {
  const name = String(row.pl ?? row.opt_name ?? "");
  const desc = String(row.desc ?? row.opt_desc ?? row.pl_desc ?? "");
  
  // Return "Name — Description" if description exists, otherwise just Name.
  // Using em dash (—) as separator which allows splitting later.
  if (name && desc) {
    return `${name} — ${desc}`;
  }
  return name;
};

/** Debug label (unused when DEBUG_COLUMNS=false) */
const formatPLDebug = (row) => {
  const id = String(row.opt_id ?? "").padStart(4, " ");
  const name = (row.pl ?? row.opt_name ?? "").slice(0, 28).padEnd(28, " ");
  const desc = (row.desc ?? row.opt_desc ?? row.pl_desc ?? "").slice(0, 34).padEnd(34, " ");
  const lbRaw = (row.limit_by_code ?? row.limit_by ?? "?").toString();
  const lb = lbRaw.toUpperCase().slice(0, 4).padEnd(4, " ");
  return `${id} | ${name} | ${desc} | ${lb}`;
};

const formatPL = (row) => (DEBUG_COLUMNS ? formatPLDebug(row) : formatPLNormal(row));

/** Format an operation option label: "<opt_name>, max m2: <max m2>" */
const formatOp = (row) => {
  const name = row.opt_name ?? "";
  const m2 = row.max_m2 != null ? String(row.max_m2) : "";
  return m2 ? `${name}, max m2: ${m2}` : name;
};

/** Robustly derive HW/SHW from any combination of fields / languages */
function deriveLimitCode(plObj) {
  if (!plObj) return "";
  let raw =
    (plObj.limit_by_code ??
      plObj.limit_by ??
      plObj.limit ??
      "").toString().trim();
  if (!raw) return "";
  const u = raw.toUpperCase();
  if (u === "HW" || u === "SHW") return u;
  if (u.includes("FABRIC") || u.includes("VẢI") || u.includes("TEXTILE")) return "SHW";
  if (u.includes("HARDWARE") || u.includes("PHỤ KIỆN") || u.includes("ACCESSORY")) return "HW";
  return u.slice(0, 3);
}

export default function SizeCheck() {
  // ---- Form (defaults)
  const [form, setForm] = useState({
    width_mm: "1500",
    height_mm: "2500",
    install: "762", // 762 = Interior, 763 = Exterior
    prod_id: "", // selected product line id (single)
    limit_by_code: "", // HW or SHW (derived from chosen product)
  });

  const [dirty, setDirty] = useState(false); // inputs changed since last Check
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showAccessoryModal, setShowAccessoryModal] = useState(false);

  // ---- HDSD State
  const [guideOpen, setGuideOpen] = useState(false);
  // Using "tools" section which describes Size Check in the JSON
  const activeGuide = HDSD_DATA["sizecheck"] || HDSD_DATA.default;

  // ---- Mobile detection (for ListBox size & marquee)
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mm = window.matchMedia("(max-width: 840px)");
    const update = () => setIsMobile(mm.matches);
    update();
    mm.addEventListener?.("change", update);
    return () => mm.removeEventListener?.("change", update);
  }, []);
  const listboxSize = isMobile ? 8 : 14;

  // ---- Derived: area (m²)
  const areaM2 = useMemo(() => {
    const w = Number(form.width_mm || 0);
    const h = Number(form.height_mm || 0);
    if (!w || !h) return 0;
    return (w * h) / 1000000;
  }, [form.width_mm, form.height_mm]);

  // ---- Data sets
  const [filteredItems, setFilteredItems] = useState([]); // filtered by size
  const [opsItems, setOpsItems] = useState([]); // operation options for selected product

  // ---- UI state
  const [loadingFiltered, setLoadingFiltered] = useState(false);
  const [loadingOps, setLoadingOps] = useState(false);
  const [error, setError] = useState("");

  // ---- Selections (SINGLE SELECTION ONLY NOW)
  const [selectedFilteredId, setSelectedFilteredId] = useState(""); // string or ""
  const [selectedOpId, setSelectedOpId] = useState(""); // string or ""

  // NEW: Additional data (light gap / headrail)
  const [lightLoading, setLightLoading] = useState(false);
  const [lightError, setLightError] = useState("");
  const [lightData, setLightData] = useState(null); // { ctrl_side, bal_side, hr_depth, hr_height, note, ... }

  // helper: find PL object by id from filtered list
  const findPLById = useCallback(
    (id) => {
      const idNum = Number(id);
      return (
        filteredItems.find((x) => Number(x.opt_id) === idNum) || null
      );
    },
    [filteredItems]
  );

  // ---- Size check (ONLY when user clicks)
  const handleCheck = async () => {
    setError("");
    setLoadingFiltered(true);
    setOpsItems([]);
    setSelectedOpId("");
    setLightData(null);
    setLightError("");

    try {
      const params = new URLSearchParams({
        action: "filter",
        width: String(form.width_mm || ""),
        height: String(form.height_mm || ""),
        install: String(form.install || ""),
      });
      if (form.prod_id) params.set("prod_id", String(form.prod_id));

      const data = await fetchJSON(`/api/size-check?${params.toString()}`);
      const items = Array.isArray(data.items) ? data.items : Array.isArray(data) ? data : [];
      setFilteredItems(items);
      setDirty(false); // inputs acknowledged

      // If a product is already selected, keep it and refresh ops
      if (form.prod_id) {
        const plObj =
          findPLById(form.prod_id) ||
          items.find((x) => String(x.opt_id) === String(form.prod_id));
        const code = deriveLimitCode(plObj);
        setForm((s) => ({ ...s, limit_by_code: code }));
        if (plObj) {
          setSelectedFilteredId(String(form.prod_id));
          await loadOpsForProduct(form.prod_id, code);
        }
      } else {
        setSelectedFilteredId("");
      }
    } catch (e) {
      setError(e.message || "Network error (size check)");
      setFilteredItems([]);
      setSelectedFilteredId("");
    } finally {
      setLoadingFiltered(false);
    }
  };

  // ---- Load operation options for the selected product
  const loadOpsForProduct = async (prodId, limitByCode) => {
    if (!prodId) {
      setOpsItems([]);
      return;
    }
    setLoadingOps(true);
    setError("");
    setSelectedOpId("");
    setLightData(null);
    setLightError("");

    try {
      const params = new URLSearchParams({
        action: "ops",
        prod_id: String(prodId),
        width: String(form.width_mm || ""),
        height: String(form.height_mm || ""),
      });
      if (limitByCode) params.set("limit_by_code", String(limitByCode));

      const data = await fetchJSON(`/api/size-check?${params.toString()}`);
      const items = Array.isArray(data.items) ? data.items : Array.isArray(data) ? data : [];
      setOpsItems(items);
    } catch (e) {
      setError(e.message || "Network error (operation options)");
      setOpsItems([]);
    } finally {
      setLoadingOps(false);
    }
  };

  // ---- Handlers (mark dirty and reset lists)
  const onChangeNumber = (key) => (e) => {
    const val = e.target.value;
    setForm((s) => ({
      ...s,
      [key]: val,
      prod_id: "",
      limit_by_code: "",
    }));
    // clear dependent state when user edits dimensions
    setSelectedFilteredId("");
    setSelectedOpId("");
    setFilteredItems([]);
    setOpsItems([]);
    setLightData(null);
    setLightError("");
    setError("");
    setDirty(true);
  };

  const onChangeInstall = (val) => {
    setForm((s) => ({
      ...s,
      install: val,
      prod_id: "",
      limit_by_code: "",
    }));
    // clear dependent state
    setSelectedFilteredId("");
    setSelectedOpId("");
    setFilteredItems([]);
    setOpsItems([]);
    setLightData(null);
    setLightError("");
    setError("");
    setDirty(true);
  };

  // SINGLE-SELECTION handler for product list
  const handleFilteredSelect = async (e) => {
    const chosen = e.target.value || "";
    const plObj = chosen ? findPLById(chosen) : null;
    const code = plObj ? deriveLimitCode(plObj) : "";

    setForm((s) => ({
      ...s,
      prod_id: chosen,
      limit_by_code: code,
    }));

    setSelectedFilteredId(chosen);
    setSelectedOpId("");
    setOpsItems([]);
    setLightData(null);
    setLightError("");

    // Fetch ops immediately for the chosen product
    if (chosen) {
      await loadOpsForProduct(chosen, code);
    }
  };

  // SINGLE-SELECTION handler for operation list
  const handleOpSelect = async (e) => {
    const chosen = e.target.value || "";
    setSelectedOpId(chosen);

    if (chosen) {
      // Only fetch lightData when we have exactly one op
      setLightLoading(true);
      setLightError("");
      setLightData(null);

      try {
        const params = new URLSearchParams({
          action: "light",
          prod_id: String(form.prod_id || ""),
          selectedopsid: String(chosen || ""),
        });
        const data = await fetchJSON(`/api/size-check?${params.toString()}`);
        setLightData(data.item || null);
      } catch (err) {
        setLightError(err.message || "Fetch failed");
        setLightData(null);
      } finally {
        setLightLoading(false);
      }
    } else {
      // cleared
      setLightData(null);
      setLightError("");
    }
  };

  // helper to compute marquee preview for product
  const previewFiltered = () => {
    if (!selectedFilteredId) return "";
    const obj = findPLById(selectedFilteredId);
    return obj ? formatPL(obj) : "";
  };

  // derive labels for downstream modal
  const productLabel = useMemo(() => {
    if (!form.prod_id) return "";
    const obj = findPLById(form.prod_id);
    return obj ? formatPL(obj) : "";
  }, [form.prod_id, findPLById]);

  const operationLabel = useMemo(() => {
    if (!selectedOpId) return "";
    const idNum = Number(selectedOpId);
    const op = opsItems.find((o) => Number(o.option_id) === idNum);
    return op ? formatOp(op) : "";
  }, [selectedOpId, opsItems]);

  // Helper to determine if Accessory Modal should be used
  const isAccessoryTrigger = () => {
    const pid = Number(form.prod_id);
    const opId = Number(selectedOpId);
    const accOps = [981, 973, 974, 1041];
    return pid === 933 && accOps.includes(opId);
  };
  
  const handleConfigClick = () => {
    if (isAccessoryTrigger()) {
      setShowAccessoryModal(true);
    } else {
      setShowConfigModal(true);
    }
  };

  /** ----------------------------
   * EXPORT helpers for config
   * -----------------------------
   */
  function tsCompact(d = new Date()) {
    const pad = (n) => String(n).padStart(2, "0");
    return (
      d.getFullYear().toString() +
      pad(d.getMonth() + 1) +
      pad(d.getDate()) +
      "_" +
      pad(d.getHours()) +
      pad(d.getMinutes()) +
      pad(d.getSeconds())
    );
  }

  function tsHuman(d = new Date()) {
    const pad = (n) => String(n).padStart(2, "0");
    return (
      d.getFullYear() +
      "-" +
      pad(d.getMonth() + 1) +
      "-" +
      pad(d.getDate()) +
      " " +
      pad(d.getHours()) +
      ":" +
      pad(d.getMinutes()) +
      ":" +
      pad(d.getSeconds())
    );
  }

  // robust extraction of pairs from SizeCheckModal payload
  function extractPairs(arrLike) {
    const arr = Array.isArray(arrLike) ? arrLike : [];
    return arr
      .map((it) => {
        if (it && (it.config != null || it.value != null)) {
          return { label: String(it.config ?? "—"), value: it.value };
        }
        const label =
          it?.label ?? it?.name ?? it?.title ?? it?.opt_name ?? it?.pl ?? "—";
        const value =
          it?.value ??
          it?.selected ??
          it?.text ??
          it?.opt_value ??
          it?.val ??
          "";
        return { label: String(label), value };
      })
      .filter((p) => {
        if (p.value == null) return false;
        if (Array.isArray(p.value)) return p.value.length > 0;
        const s = String(p.value);
        return s.trim() !== "";
      });
  }

  function splitRequiredOptional(payload) {
    const items = Array.isArray(payload?.items) ? payload.items : [];

    if (items.length) {
      const reqItems = items.filter(
        (x) => String(x.section || "").toLowerCase() === "default"
      );
      const optItems = items.filter(
        (x) => String(x.section || "").toLowerCase() === "ops"
      );
      return {
        required: extractPairs(reqItems),
        optional: extractPairs(optItems),
      };
    }

    return {
      required: extractPairs(payload?.required),
      optional: extractPairs(payload?.optional),
    };
  }

  function joinLines(pairs) {
    if (!pairs || pairs.length === 0) return "- (none)";
    return pairs
      .map((p) => `- ${p.label}: ${Array.isArray(p.value) ? p.value.join(", ") : p.value}`)
      .join("\n");
  }

  // Download config text using template
  const handleDownloadConfig = async (payload) => {
    // enforce: must have exactly one operation
    if (!selectedOpId) {
      setError(
        "Vui lòng chọn đúng 1 lựa chọn vận hành / Please select exactly 1 operation option."
      );
      return;
    }

    try {
      const tplResp = await fetch("/product_order_config.json", {
        cache: "no-store",
      });
      if (!tplResp.ok) throw new Error(`Template HTTP ${tplResp.status}`);
      const tpl = await tplResp.json();

      const now = new Date();

      const fullLabel = 
        payload?.prod_label ||
        productLabel ||
        (form.prod_id ? `prod-${form.prod_id}` : "product");

      // Clean the prod_name to ensure filenames are valid/short. 
      // Splits at the em dash ("—") if present.
      const prod_name = fullLabel.split("—")[0].trim();

      const prod_id_val = payload?.prod_id || form.prod_id || "";
      const op_label =
        payload?.operation_label || operationLabel || "operation";

      const { required, optional } = splitRequiredOptional(payload || {});
      const required_lines = joinLines(required);
      const optional_lines = joinLines(optional);

      const data = {
        prod_name,
        prod_id: prod_id_val,
        op_label,
        width_mm: form.width_mm || "",
        height_mm: form.height_mm || "",
        ts: tsCompact(now),
        ts_human: tsHuman(now),
        required_lines,
        optional_lines,
      };

      const body = render(String(tpl.body || ""), data);
      const filename = renderFilename(
        String(tpl.filename || "export_{{ts}}.txt"),
        data
      );

      if (!window.dl_prod_ord_config_txt) {
        throw new Error(
          "Thiếu helper tải xuống (dl_prod_ord_config_txt.js) / Missing downloader helper."
        );
      }

      // Add BOM to help diacritics in Windows editors
      window.dl_prod_ord_config_txt({
        filename,
        text: body,
        addBOM: true,
      });
    } catch (e) {
      setError(
        e.message ||
          "Lỗi xuất tập tin cấu hình / Export failed"
      );
    }
  };

  return (
    <div className="app-page">
      <div
        className="size-check"
        style={{
          maxWidth: 1100,
          margin: "0 auto",
          padding: isMobile ? "8px 10px" : "0 0",
        }}
      >
        {/* Page-scoped CSS for responsive layout */}
        <style>{`
          @keyframes marqueeX { 0% { transform: translateX(0); } 100% { transform: translateX(-100%); } }

          .sc-top-grid {
            display: grid;
            grid-template-columns: minmax(320px, 520px) 1fr;
            gap: 16px;
            align-items: start;
          }
          .sc-lists-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 16px;
            margin-top: 18px;
          }
                      
          /* Force equal width for both panels under all screen sizes */
          .sc-lists-grid {
            align-items: stretch;
            justify-content: center;
          }

          .sc-lists-grid > .sc-panel {
            width: 100%;
            min-width: 0;
          }

          /* On desktop, both equal 50% */
          @media (min-width: 841px) {
            .sc-lists-grid {
              grid-template-columns: repeat(2, 1fr);
            }
            .sc-lists-grid > .sc-panel {
              flex: 1 1 50%;
            }
          }

          /* On mobile (stacked), full width each */
          @media (max-width: 840px) {
            .sc-lists-grid {
              grid-template-columns: 1fr;
            }
            .sc-lists-grid > .sc-panel {
              width: 100%;
            }
          }

          .sc-panel { padding: 10px 12px; border: 1px solid #e5e7eb; border-radius: 8px; }
          .sc-note { font-size: 12px; opacity: 0.8; margin-top: 8px; }
          .sc-heading { font-weight: 600; margin-bottom: 8px; }

          /* Mobile-first adjustments */
          @media (max-width: 840px) {
            h2 {
              font-size: 1.05rem;
              line-height: 1.25;
              margin-bottom: 10px;
              word-break: break-word;
            }
            .sc-top-grid { grid-template-columns: 1fr; }
            .sc-lists-grid { grid-template-columns: 1fr; }
            .sc-panel { padding: 8px 10px; }
            .sc-compact p,
            .sc-compact div,
            .sc-compact label {
              font-size: 13px !important;
            }
            .sc-additional { font-size: 12px; }
            .sc-marquee { display: none; }
          }
        `}</style>

        <h2>
          Kiểm tra kích thước — Sản phẩm phù hợp / Size Check — Applicable
          Products
        </h2>

        {/* TOP: inputs + Additional data on the right (stack on mobile) */}
        <div className="sc-top-grid sc-compact">
          {/* Inputs */}
          <div>
            <div className="grid" style={{ display: "grid", gap: 12 }}>
              <TextInput
                label="Chiều rộng / Width (mm)"
                type="number"
                value={form.width_mm}
                onChange={onChangeNumber("width_mm")}
                min={0}
                helper={`≈ ${(Number(form.width_mm || 0) / 25.4).toFixed(
                  2
                )} in | ½: ${Math.floor(Number(form.width_mm || 0) / 2)} mm | ⅓: ${Math.floor(Number(form.width_mm || 0) / 3)} mm`}
              />

<TextInput
                label="Chiều cao / Height (mm)"
                type="number"
                value={form.height_mm}
                onChange={onChangeNumber("height_mm")}
                min={0}
                helper={`≈ ${(Number(form.height_mm || 0) / 25.4).toFixed(
                  2
                )} in | ½: ${Math.floor(Number(form.height_mm || 0) / 2)} mm | ⅓: ${Math.floor(Number(form.height_mm || 0) / 3)} mm`}
              />

              <RadioGroup
                legend="Sản phẩm lắp đặt / Product install"
                name="install"
                value={form.install}
                onChange={onChangeInstall}
                options={[
                  { value: "762", label: "Trong nhà / Interior" },
                  { value: "763", label: "Ngoài trời / Exterior" },
                ]}
              />

              {/* Check button & HDSD */}
              <div style={{ marginTop: 10 }}>
                <Button onClick={handleCheck} disabled={loadingFiltered}>
                  {loadingFiltered
                    ? "Đang kiểm tra..."
                    : "Kiểm tra / Check"}
                </Button>{" "}
                <Button onClick={() => setGuideOpen(true)} style={{ marginLeft: 6 }}>
                  HDSD
                </Button>
              </div>
            </div>

            {dirty && (
              <div
                style={{
                  marginTop: 8,
                  fontSize: 12,
                  color: "#b45309",
                }}
              >
                • Thay đổi đầu vào — vui lòng bấm{" "}
                <b>Kiểm tra / Check</b> để tải dữ liệu.
              </div>
            )}
          </div>

          {/* RIGHT / BELOW on mobile: Additional data panel */}
          <div
            className="sc-panel sc-additional"
            style={{
              borderStyle: "dashed",
              minHeight: 92,
            }}
          >
            <div
              style={{
                fontSize: 12,
                opacity: 0.8,
                marginBottom: 4,
              }}
            >
              Thông tin bổ sung / Additional data
            </div>
            {lightLoading ? (
              <div style={{ fontSize: 12 }}>Đang tải…</div>
            ) : lightError ? (
              <div
                style={{
                  color: "crimson",
                  fontSize: 12,
                }}
              >
                Lỗi: {lightError}
              </div>
            ) : lightData ? (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 6,
                  fontSize: 13,
                }}
              >
                <div>
                  Hở sáng bên điều khiển / Light gap (ctrl):
                  <b> {lightData.ctrl_side ?? "-"}</b>
                </div>
                <div>
                  Hở sáng bên còn lại / Light gap (other):
                  <b> {lightData.bal_side ?? "-"}</b>
                </div>
                <div>
                  Độ sâu ray đỉnh / Headrail depth:
                  <b> {lightData.hr_depth ?? "-"}</b>
                </div>
                <div>
                  Chiều cao ray đỉnh / Headrail height:
                  <b> {lightData.hr_height ?? "-"}</b>
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  Ghi chú / Note:{" "}
                  <i>{lightData.note || "—"}</i>
                </div>

                {(lightData.min_width !== undefined ||
                  lightData.min_height !== undefined ||
                  lightData.maxof_max_width !== undefined ||
                  lightData.maxof_max_height !== undefined ||
                  lightData.max_m2 !== undefined) && (
                  <div
                    style={{
                      gridColumn: "1 / -1",
                      borderTop: "1px solid #ddd",
                      marginTop: 6,
                      paddingTop: 4,
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 600,
                        marginBottom: 2,
                      }}
                    >
                      Giới hạn kích thước / Size
                      limits
                    </div>
                    <div>
                      Rộng tối thiểu / Min
                      width:{" "}
                      <b>
                        {lightData.min_width ??
                          "—"}
                      </b>{" "}
                      mm
                      <br />
                      Cao tối thiểu / Min
                      height:{" "}
                      <b>
                        {lightData.min_height ??
                          "—"}
                      </b>{" "}
                      mm
                    </div>
                    <div style={{ marginTop: 4 }}>
                      Rộng tối đa / Max
                      width:{" "}
                      <b>
                        {lightData.maxof_max_width ??
                          "—"}
                      </b>{" "}
                      mm
                      <br />
                      Cao tối đa / Max
                      height:{" "}
                      <b>
                        {lightData.maxof_max_height ??
                          "—"}
                      </b>{" "}
                      mm
                    </div>
                    <div style={{ marginTop: 4 }}>
                      Diện tích tối đa / Max
                      area:{" "}
                      <b>
                        {lightData.max_m2 ??
                          "—"}
                      </b>{" "}
                      m²
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div
                style={{
                  fontSize: 12,
                  opacity: 0.75,
                }}
              >
                Chọn 1 lựa chọn vận hành để xem
                chi tiết. / Select one
                operation option to see
                details.
              </div>
            )}
          </div>
        </div>

        {/* Lists: stack on mobile */}
        <div className="sc-lists-grid">
          {/* Product list */}
          <div className="sc-panel">
            <div className="sc-heading">
              1) Sản phẩm -{" "}
              {form.install === "762"
                ? "Trong nhà"
                : "Ngoài trời"}{" "}
              - đề xuất
            </div>

            {DEBUG_COLUMNS && (
              <div
                style={{
                  fontSize: 12,
                  opacity: 0.75,
                  marginBottom: 6,
                  fontFamily:
                    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                }}
              >
                {
                  "ID  | NAME                        | DESCRIPTION                         | LB"
                }
              </div>
            )}

            <ListBox
              /* single selection now, no "multiple" prop */
              size={listboxSize}
              value={selectedFilteredId ? [selectedFilteredId] : []}
              onChange={handleFilteredSelect}
            >
              {filteredItems.length === 0 ? (
                <option disabled>
                  Hãy nhập kích thước & bấm Kiểm tra /
                  Enter sizes & press Check
                </option>
              ) : (
                filteredItems.map((item) => (
                  <option
                    key={String(item.opt_id)}
                    value={String(item.opt_id)}
                    title={formatPL(item)}
                  >
                    {formatPL(item)}
                  </option>
                ))
              )}
            </ListBox>

            {/* preview ticker for chosen product */}
            {selectedFilteredId && (
              <div
                className="sc-marquee"
                style={{
                  marginTop: 6,
                  border: "1px dashed #e5e7eb",
                  borderRadius: 6,
                  overflow: "hidden",
                  padding: "3px 6px",
                  fontSize: 12,
                }}
              >
                <div style={{ whiteSpace: "nowrap" }}>
                  <span
                    style={{
                      display: "inline-block",
                      paddingLeft: "100%",
                      animation:
                        "marqueeX 12s linear infinite",
                    }}
                  >
                    {previewFiltered()}
                  </span>
                </div>
              </div>
            )}

            <div className="sc-note">
              Đã chọn / Selected:{" "}
              {form.prod_id
                ? `${form.prod_id} (${form.limit_by_code || "?"})`
                : "(chưa chọn / none)"}
            </div>
          </div>

          {/* Operation options */}
          <div className="sc-panel">
            <div className="sc-heading" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>2) Vận hành - Kết quả có thể áp dụng</span>
              {/* CONFIG BUTTON MOVED HERE */}
              {form.prod_id && selectedOpId && (
                <Button 
                  onClick={handleConfigClick}
                  style={{ padding: "4px 8px", fontSize: "13px" }}
                >
                  Cấu hình SP để đặt hàng
                </Button>
              )}
            </div>

            <ListBox
              /* single selection now, no "multiple" prop */
              size={listboxSize}
              value={selectedOpId ? [selectedOpId] : []}
              onChange={handleOpSelect}
              disabled={!form.prod_id || loadingOps}
            >
              {opsItems.length === 0 ? (
                <option disabled>
                  {form.prod_id
                    ? loadingOps
                      ? "Đang tải lựa chọn vận hành…"
                      : "Không có lựa chọn phù hợp / No applicable options"
                    : "Hãy chọn 1 sản phẩm ở danh sách bên trái / Select one product on the left"}
                </option>
              ) : (
                opsItems.map((op) => (
                  <option
                    key={String(op.option_id)}
                    value={String(op.option_id)}
                  >
                    {formatOp(op)}
                  </option>
                ))
              )}
            </ListBox>

            <div className="sc-note">
              Đã chọn / Selected:{" "}
              {selectedOpId || "(chưa chọn / none)"}
            </div>
          </div>
        </div>

        {/* Area only */}
        <div style={{ marginTop: 14 }}>
          Mét vuông / Calculated area:{" "}
          <b>{areaM2.toFixed(2)}</b> m²
        </div>

        {error && (
          <div
            style={{
              color: "crimson",
              marginTop: 10,
            }}
          >
            ✖ Lỗi / Error: {error}
          </div>
        )}

        <div
          style={{
            fontSize: 12,
            opacity: 0.7,
            marginTop: 16,
          }}
        >
          Nguồn / Source: Đầu vào / Input:
          {" "}
          W {form.width_mm} mm × H{" "}
          {form.height_mm} mm →{" "}
          {areaM2.toFixed(2)} m² | Lắp đặt /
          Install:{" "}
          {form.install === "762"
            ? "Trong nhà / Interior"
            : "Ngoài trời / Exterior"}
        </div>
      </div>

      {/* Product Configuration Modal */}
      <SizeCheckModal
        open={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        width_mm={form.width_mm}
        height_mm={form.height_mm}
        prod_id={form.prod_id}
        prod_label={productLabel}
        operation_id={selectedOpId}
        operation_label={operationLabel}
        /* Downstream expects an array for selectedOpIds, keep that shape: */
        selectedOpIds={selectedOpId ? [selectedOpId] : []}
        onDownload={handleDownloadConfig}
        /* Pass HW/SHW so modal can decide about Fabric Collection */
        limit_by_code={form.limit_by_code}
      />

      <AccessoryModal
        open={showAccessoryModal}
        onClose={() => setShowAccessoryModal(false)}
        prod_id={form.prod_id}
        prod_label={productLabel}
        operation_id={selectedOpId}
        operation_label={operationLabel}
        selectedOpIds={selectedOpId ? [selectedOpId] : []}
      />

      {/* --- GUIDE MODAL --- */}
      <Modal
        open={guideOpen}
        title={activeGuide.title}
        onClose={() => setGuideOpen(false)}
        maxWidth={600}
      >
         <div className="box" style={{ margin: 0, border: "none", padding: 0 }}>
            <div style={{ marginBottom: 16 }}>
              {activeGuide.intro}
            </div>
            <ul style={{ paddingLeft: 20, margin: 0 }}>
              {activeGuide.steps.map((line, i) => (
                <li key={i} style={{ marginBottom: 12, whiteSpace: "pre-wrap" }}>
                  {line}
                </li>
              ))}
            </ul>
         </div>
      </Modal>

    </div>
  );
}